'use client'

/**
 * Purpose: Survey page 2 — self-assessed knowledge per skin axis (yes/no toggles).
 * Inputs: formState.skinKnowledge, setKnowledge handler, translated copy.
 * Outputs: Four yes/no button pairs that gate slider vs questionnaire on later pages.
 */

import type { FormState } from '@lib/models/types'
import { getSkinKnowledgeValue } from '@lib/survey-axis-maps'

// One row per skin axis — order matches later questionnaire pages.
const KNOWLEDGE_AXES = [
  { axis: 'oilyDry', questionKey: 'skinKnowledge.oilyDryQuestion' },
  { axis: 'sensitiveResistant', questionKey: 'skinKnowledge.sensitiveResistantQuestion' },
  { axis: 'pigmented', questionKey: 'skinKnowledge.pigmentedQuestion' },
  { axis: 'wrinkled', questionKey: 'skinKnowledge.wrinkledQuestion' },
] as const

// Page shell — maps each axis to a yes/no self-knowledge toggle.
export function SurveyKnowledgePage({
  formState,
  setKnowledge,
  title,
  description,
  yesLabel,
  noLabel,
  tPages,
}: {
  formState: FormState
  setKnowledge: (axis: keyof FormState['skinKnowledge'], value: boolean) => void
  title: string
  description: string
  yesLabel: string
  noLabel: string
  tPages: (key: string) => string
}) {
  // Four yes/no gates — "no" unlocks the detailed axis questionnaire later.
  return (
    <div id="survey-knowledge-page">
      <h1 id="survey-knowledge-page-title" className="mb-2">
        {title}
      </h1>
      <p id="survey-knowledge-page-description" className="text-black text-sm mb-8">
        {description}
      </p>
      {KNOWLEDGE_AXES.map(({ axis, questionKey }) => {
        const selected = getSkinKnowledgeValue(formState.skinKnowledge, axis)
        return (
          <div key={axis} id={`survey-knowledge-${axis}`} className="mb-6 p-4">
            {/* Question text doubles as the group's accessible name via aria-labelledby. */}
            <p id={`survey-knowledge-${axis}-question`} className="question-bold text-black mb-3">
              {tPages(questionKey)}
            </p>
            {/* Exception: toggle buttons with selected/unselected state.
                No-selected uses bg-black (negative answer affordance) — no HoverGradientButton variant exists for this. */}
            <div
              id={`survey-knowledge-${axis}-options`}
              role="group"
              aria-labelledby={`survey-knowledge-${axis}-question`}
              className="flex gap-3"
            >
              <button
                id={`survey-knowledge-${axis}-yes`}
                type="button"
                onClick={() => setKnowledge(axis, true)}
                aria-pressed={selected === true}
                className={`flex-1 py-2 px-4 rounded-full text-sm font-semibold border transition-colors cursor-pointer ${
                  selected === true
                    ? 'bg-orange text-white border-orange'
                    : 'bg-transparent text-orange border-orange'
                }`}
              >
                {yesLabel}
              </button>
              {/* No = user wants the detailed questionnaire for this axis later. */}
              <button
                id={`survey-knowledge-${axis}-no`}
                type="button"
                onClick={() => setKnowledge(axis, false)}
                aria-pressed={selected === false}
                className={`flex-1 py-2 px-4 rounded-full text-sm font-semibold border transition-colors cursor-pointer ${
                  selected === false
                    ? 'bg-black text-white border-black'
                    : 'bg-transparent text-orange border-orange hover:border-black'
                }`}
              >
                {noLabel}
              </button>
            </div>
          </div>
        )
      })}
    </div>
  )
}
